import { api, Product, Sale as APISale, SaleProduct } from './api';

export type { APISale as Sale };

export interface SaleItem {
  productId: string;
  quantity: number;
  price: number;
}

export interface CreateSaleDto {
  companyId: string;
  products: SaleItem[];
  totalAmount: number;
  clientName?: string;
  notes?: string;
  userId?: string;
  paymentMode?: string;
  status?: string;
  discount?: number;
}

export const salesService = {
  createSale: async (saleData: CreateSaleDto): Promise<APISale> => {
    try {
      // Vérifier que les données requises sont présentes
      if (!saleData.companyId) {
        throw new Error('L\'ID de l\'entreprise est requis');
      }
      if (!saleData.products || saleData.products.length === 0) {
        throw new Error('Les produits sont requis');
      }
      if (typeof saleData.totalAmount !== 'number' || saleData.totalAmount < 0) {
        throw new Error('Le total doit être un nombre positif');
      }

      // Vérifier que chaque produit a les propriétés requises
      saleData.products.forEach((product: SaleItem, index: number) => {
        if (!product.productId) {
          throw new Error(`L'ID du produit est manquant pour le produit ${index + 1}`);
        }
        if (typeof product.quantity !== 'number' || product.quantity <= 0) {
          throw new Error(`La quantité doit être un nombre positif pour le produit ${index + 1}`);
        }
        if (typeof product.price !== 'number' || product.price < 0) {
          throw new Error(`Le prix doit être un nombre positif pour le produit ${index + 1}`);
        }
      });

      // Transformer les SaleItem en SaleProduct
      const transformedProducts: SaleProduct[] = saleData.products.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.price,
        total: item.price * item.quantity
      }));

      // Ajouter les valeurs par défaut
      const completeData = {
        ...saleData,
        products: transformedProducts,
        userId: saleData.userId || 'default',
        paymentMode: saleData.paymentMode || 'cash',
        status: saleData.status || 'completed',
        discount: saleData.discount || 0
      };

      const response = await api.createSale(completeData);
      return response;
    } catch (error) {
      console.error('Error creating sale:', error);
      throw error;
    }
  },

  getSales: async (companyId: string): Promise<APISale[]> => {
    try {
      return await api.getSales(companyId);
    } catch (error) {
      console.error('Error fetching sales:', error);
      throw error;
    }
  }
};

export default salesService;
